import { useState, useEffect } from "react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { DialogFooter } from "@/shared/components/ui/dialog";
import {
  DOCUMENT_TYPES,
  LEGAL_CATEGORIES,
} from "@/shared/constants/institutionDocuments";
import type { PastaInstituicao } from "../hooks/usePastasInstituicao";
import { supabase } from "@/shared/integrations/supabase/client";
import { Loader2 } from "lucide-react";

export interface SuggestArchivingMeta {
  title?: string;
  document_type?: string;
  legal_category?: string;
  folder?: string;
  summary?: string;
}

interface ArchivingModalFormProps {
  institutionId: string;
  initialMeta: SuggestArchivingMeta | null;
  file: File | null;
  onSuccess: () => void;
  onCancel: () => void;
  toast: (p: {
    title: string;
    description?: string;
    variant?: "default" | "destructive";
  }) => void;
  pastas?: PastaInstituicao[];
  /** Portal externo / instituição: envia para fila de revisão do escritório (status em_revisao). */
  enviarParaRevisaoEscritorio?: boolean;
}

function ArchivingModalForm({
  institutionId,
  initialMeta,
  file,
  onSuccess,
  onCancel,
  toast,
  pastas,
  enviarParaRevisaoEscritorio = false,
}: ArchivingModalFormProps) {
  const [title, setTitle] = useState(initialMeta?.title ?? "");
  const [documentType, setDocumentType] = useState(
    initialMeta?.document_type ?? "outro"
  );
  const [legalCategory, setLegalCategory] = useState(
    initialMeta?.legal_category ?? ""
  );
  const [folder, setFolder] = useState(initialMeta?.folder ?? "");
  const [summary, setSummary] = useState(initialMeta?.summary ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialMeta) {
      setTitle(initialMeta.title ?? "");
      setDocumentType(initialMeta.document_type ?? "outro");
      setLegalCategory(initialMeta.legal_category ?? "");
      setFolder(initialMeta.folder ?? "");
      setSummary(initialMeta.summary ?? "");
    }
  }, [initialMeta]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({ title: "Título é obrigatório", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      let attachmentUrl: string | null = null;
      if (file) {
        const ext = file.name.split(".").pop() || "bin";
        const path = `${institutionId}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("institution-documents")
          .upload(path, file, { upsert: false });
        if (upErr) throw new Error(upErr.message);
        attachmentUrl = path;
      }
      const body = summary.trim() || "";
      const now = new Date().toISOString();
      const folderSlug = folder || null;
      const payload: Record<string, unknown> = {
        institution_id: institutionId,
        title: title.trim(),
        content: body,
        document_type: documentType,
        legal_category: legalCategory || null,
        folder: folderSlug,
        version: "1.0",
        is_active: true,
        created_by: user?.id ?? null,
        attachment_url: attachmentUrl,
        extraction_status: "concluido",
        extraction_method: "manual",
        extracted_chars: body.length,
        extracted_at: now,
      };
      if (enviarParaRevisaoEscritorio) {
        payload.status = "em_revisao";
        payload.review_required = true;
        payload.target_folder_slug = folderSlug;
      }
      const { error } = await supabase.from("institution_documents").insert(payload);
      if (error) throw new Error(error.message);
      onSuccess();
    } catch (e) {
      toast({
        title: "Erro ao arquivar",
        description: e instanceof Error ? e.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 py-2">
      <div className="space-y-2">
        <Label>Título</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Título do documento"
        />
      </div>
      <div className="space-y-2">
        <Label>Tipo de documento</Label>
        <Select value={documentType} onValueChange={setDocumentType}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DOCUMENT_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Categoria jurídica (opcional)</Label>
        <Select
          value={legalCategory || "_"}
          onValueChange={(v) => setLegalCategory(v === "_" ? "" : v)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecione" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_">Nenhuma</SelectItem>
            {LEGAL_CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Pasta (opcional)</Label>
        <Select value={folder || "_"} onValueChange={(v) => setFolder(v === "_" ? "" : v)}>
          <SelectTrigger>
            <SelectValue placeholder="Selecione" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_">Nenhuma</SelectItem>
            {(pastas && pastas.length > 0 ? pastas : []).map((f) => (
              <SelectItem key={f.id} value={f.slug}>
                {f.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Resumo (opcional, usado como conteúdo)</Label>
        <Input
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="Resumo"
        />
      </div>
      {file && (
        <p className="text-xs text-muted-foreground">Arquivo: {file.name}</p>
      )}
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={saving}
        >
          Cancelar
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={saving}>
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            "Arquivar"
          )}
        </Button>
      </DialogFooter>
    </div>
  );
}

export { ArchivingModalForm };
export default ArchivingModalForm;
